import type { ControlInfo, ReadOnlyRibbonControlDefProps } from 'owa-mail-ribbon-utils';
import type { MailboxInfo } from 'owa-client-types';
import { constructKeytip, getActionSourceFromCommandingViewMode } from 'owa-command-ribbon';
import {
    createMailAppButton,
    createMailAppFlyoutAnchor,
} from 'owa-mail-ribbon-utils/lib/createMailRibbonButton';
import { getDefaultSlrAppRibbonButtonStyles } from 'owa-command-ribbon-styles';
import { resetMenuDefinitionMutator } from '../mutatorActions/menuDefinitionMutatorActions';
import { type MailRibbonControlId, type MailRibbonTabId } from 'owa-ribbon-ids/lib/mailRibbonId';
import type { RibbonControlProps } from 'owa-acui';
import { getMenuDefinition } from '../store/menuDefinitionStore';
import { getSharedControlInfo } from 'owa-mail-ribbon-utils/lib/getSharedControlInfo';
import { getReadRibbonId } from 'owa-mail-ribbon-utils/lib/getReadRibbonId';
import { getRibbonMailboxInfo } from 'owa-mail-ribbon-utils/lib/getRibbonMailboxInfo';
import { resetMoveToSearch } from 'owa-mail-ribbon-command-actions/lib/resetMoveToSearch';
import { onMoveToMenuClicked } from '../components/utils/deferredOnMoveToMenuClicked';
import { isUnifiedInboxMailboxInfo } from 'owa-client-types/lib/UnifiedInboxMailboxInfo';
import {
    getSelectedTableView,
    isMultiAccountTable,
    tryGetMailboxInfoFromRowKeysOrIdsIfUnifiedInboxOrDefault,
} from 'owa-mail-list-store';
import { lazyShowMoveToFolderDialogV9 } from 'owa-mail-folder-moveto-dialog';
import { MoveToFolderScenario } from 'owa-mail-moveto-control-types';
import { type LoadMoreFoldersActionSource } from 'owa-folders-constants';
import { lazyMoveMailListRows } from 'owa-mail-triage-action';
import { resetFocus } from 'owa-mail-focus-manager';
import getEffectiveRowKeys from 'owa-mail-list-store/lib/utils/getEffectiveRowKeys';
import { getFolderIdOrUnifiedInboxFolderId } from 'owa-unified-inbox-data-utils/lib/getFolderIdOrUnifiedInboxFolderId';

interface CreateMoveControlOptions {
    /** Whether this is for SLR (affects styling) */
    isSLR?: boolean;
    /** Whether to include itemId in button ID (for projection scenarios) */
    includeItemId?: boolean;
}

function shouldShowMoveToDialogDirectlyForRibbon(mailboxInfo: MailboxInfo): boolean {
    return isUnifiedInboxMailboxInfo(mailboxInfo) || isMultiAccountTable(getSelectedTableView());
}

/**
 * Launch the Move to dialog directly from ribbon context. Used for unified inbox, which skips ribbon flyout
 * @param mailboxInfo The mailbox info to launch the dialog with
 * @param isCopy Whether this is a copy operation (default false for move)
 */
function launchMoveToDialogFromRibbon(mailboxInfo: MailboxInfo, isCopy: boolean = false): void {
    const tableView = getSelectedTableView();
    const selectedRowKeys = getEffectiveRowKeys(tableView);

    // Resolve actual mailboxInfo instead of UIB or the module mailbox in MAS.
    let actualMailboxInfo = mailboxInfo;
    if (
        (isUnifiedInboxMailboxInfo(mailboxInfo) || isMultiAccountTable(tableView)) &&
        tableView &&
        selectedRowKeys?.length > 0
    ) {
        try {
            actualMailboxInfo = tryGetMailboxInfoFromRowKeysOrIdsIfUnifiedInboxOrDefault(
                mailboxInfo,
                selectedRowKeys,
                tableView
            );
        } catch {
            // Fall back to UIB mailboxInfo if resolution fails.
            // The dialog will still open successfully, just may not have the right folders expanded
        }
    }

    // Get the actual parent folder instead of unified inbox folder
    const actualSourceFolderId = getFolderIdOrUnifiedInboxFolderId(
        tableView?.tableQuery.folderId,
        actualMailboxInfo
    );

    lazyShowMoveToFolderDialogV9.importAndExecute({
        targetWindow: window.parent || window,
        mailboxInfo: actualMailboxInfo,
        scenario: isCopy ? MoveToFolderScenario.CopyTo : MoveToFolderScenario.MoveTo,
        source: 'Ribbon',
        selectorProps: {
            sourceFolderId: actualSourceFolderId,
            supportedMailBoxTypes: ['UserMailbox', 'ArchiveMailbox', 'SharedMailbox'],
            onFolderClick: (destinationFolderId: string, _destinationMailboxInfo: MailboxInfo) => {
                if (isCopy) {
                    // For now, copy isn't supported from ribbon - just handle move
                    return;
                }

                if (selectedRowKeys && selectedRowKeys.length > 0) {
                    lazyMoveMailListRows.importAndExecute(
                        selectedRowKeys,
                        destinationFolderId,
                        tableView?.id || '',
                        getActionSourceFromCommandingViewMode()
                    );
                }
                resetFocus('ribbonMoveMenuDismissed');
            },
            excludeSearchFolders: true,
            excludeNotesFolder: true,
            includeAllAccounts: true,
            loadMoreActionSource: 'Ribbon',
        },
    });
}

/**
 * Creates a Move control that either shows as a direct dialog button (for unified inbox)
 * or as a flyout anchor with menu (for regular scenarios).
 */
export function createMoveControl(
    keytipTabId: MailRibbonTabId | undefined,
    props: ReadOnlyRibbonControlDefProps,
    options: CreateMoveControlOptions = {}
): RibbonControlProps {
    const { isSLR = true, includeItemId = false } = options;

    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo: ControlInfo = getSharedControlInfo(540, props);
    const shouldShowDialogDirectly = shouldShowMoveToDialogDirectlyForRibbon(mailboxInfo);

    const buttonId = includeItemId ? getReadRibbonId(540, props.itemId) : 540;

    const keytipArray = keytipTabId ? [keytipTabId] : [];

    // If the ribbon button will open the move dialog instead of a flyout, it should be a regular button
    if (shouldShowDialogDirectly) {
        return createMailAppButton(
            buttonId,
            controlInfo.text,
            controlInfo.tooltip,
            props,
            () => {
                launchMoveToDialogFromRibbon(mailboxInfo);
            },
            constructKeytip(keytipArray, 540),
            controlInfo.iconName,
            undefined /*iconColor*/,
            isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
            includeItemId ? props.itemId : undefined
        );
    }

    // Otherwise, create a flyout anchor with menu
    const storeName = 'MoveToMenuDefinitionStore';
    return createMailAppFlyoutAnchor(
        buttonId,
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getMenuDefinition(storeName, props.projectionTabId),
        mailboxInfo,
        constructKeytip(keytipArray, 540, true /* hasMenu */),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        includeItemId ? props.itemId : undefined,
        {
            delayUpdateFocusOnHover: true,
            onExecute: () => {
                resetMoveToSearch(true /*onEnter*/, props.projectionTabId);
                onMoveToMenuClicked(keytipTabId, props, mailboxInfo);
            },
            onAfterMenuDismiss: () => {
                resetMoveToSearch(false /* onEnter */, props.projectionTabId);
                resetMenuDefinitionMutator(storeName, props.projectionTabId);
            },
            shouldFocusOnMount: false, // To not override searchbox focus
            shouldFocusOnContainer: false, // To not override searchbox focus
            enableDownArrowNavigation: true, // to allow the searchbox lose its focus on the menu and enable the down arrow key navigation
        }
    );
}
